/**
 * openF1LiveClient — live session state + car telemetry for the track map.
 * -------------------------------------------------------------------------
 * WHY A SEPARATE CLIENT (not openF1Client.js):
 *   - openF1Client.js talks to https://api.openf1.org directly (CORS-open) for
 *     the Oracle's fastest-lap lookups, and stays key-free.
 *   - The LIVE map needs data DURING a session, which OpenF1 gates behind a
 *     paid key in 2026 (every endpoint answers 401 "Live F1 session in
 *     progress" while a session runs). A key MUST NOT ship in the browser
 *     bundle, so these calls route through the /api/openf1 dev proxy, which
 *     injects the key server-side (see scripts/vite-openf1-proxy.mjs).
 *
 * STATE MODEL (fetchLiveState):
 *   'live'    — a session is running AND we can read telemetry (free window, or
 *               a key is configured). The map shows real cars.
 *   'locked'  — a session is running but OpenF1 returned the 401 live-block and
 *               no key is set. We know a session is on; positions are withheld.
 *               The map shows the circuit shape + an honest "unlocks after the
 *               session / with access" message. Never faked.
 *   'offline' — no session in progress. The LIVE pill stays hidden.
 *
 * PRODUCTION: like the article reader, /api/openf1 exists only in the dev
 * server. A packaged build has no such origin, so the live map degrades to
 * 'offline'. Port the proxy to the same edge function as the other rewrites to
 * ship it — see README → Backend integration.
 */
import { latestPerDriver } from './circuitGeometry.js';

const BASE = '/api/openf1';

/** Tolerance around the scheduled window so we count as "live" a touch early/late. */
const PREROLL_MS = 10 * 60 * 1000;
const POSTROLL_MS = 5 * 60 * 1000;

const CACHE = new Map();

/**
 * Low-level GET that never throws on HTTP status — the 401 live-block is a
 * signal we want to read, not an error. Returns { ok, status, body }.
 */
async function rawGet(path, { signal, ttl = 0 } = {}) {
  if (ttl > 0) {
    const c = CACHE.get(path);
    if (c && c.expiresAt > Date.now()) return c.value;
  }
  let res;
  try {
    res = await fetch(`${BASE}${path}`, { signal, headers: { Accept: 'application/json' } });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return { ok: false, status: 0, body: null, networkError: true };
  }
  const body = await res.json().catch(() => null);
  const value = { ok: res.ok, status: res.status, body };
  if (ttl > 0 && res.ok) CACHE.set(path, { value, expiresAt: Date.now() + ttl });
  return value;
}

/** True when a response body is OpenF1's "live session in progress" 401 block. */
function isLiveBlock(status, body) {
  if (status !== 401) return false;
  const detail = typeof body?.detail === 'string' ? body.detail.toLowerCase() : '';
  return detail.includes('live') && detail.includes('session');
}

/** Is `session` running right now (with pre/post tolerance)? */
function isWithinWindow(session, now = Date.now()) {
  const start = Date.parse(session?.date_start);
  const end = Date.parse(session?.date_end);
  if (!Number.isFinite(start)) return false;
  if (now < start - PREROLL_MS) return false;
  if (Number.isFinite(end) && now > end + POSTROLL_MS) return false;
  return true;
}

/**
 * fetchLiveState — the single call the LIVE pill polls.
 * @returns {Promise<{status:'live'|'locked'|'offline', session:object|null, reason?:string}>}
 */
export async function fetchLiveState({ signal, now = Date.now() } = {}) {
  const res = await rawGet('/sessions?session_key=latest', { signal });

  if (isLiveBlock(res.status, res.body)) {
    // A session is definitely live (that's the only time OpenF1 blocks), but we
    // have no key so we can't read it — and can't learn the circuit from OpenF1.
    return { status: 'locked', session: null, reason: 'openf1-live-block' };
  }
  if (!res.ok || res.body == null) {
    return { status: 'offline', session: null, reason: res.networkError ? 'network' : `http-${res.status}` };
  }
  const session = Array.isArray(res.body) ? res.body[res.body.length - 1] : res.body;
  if (!session) return { status: 'offline', session: null, reason: 'no-session' };

  return { status: isWithinWindow(session, now) ? 'live' : 'offline', session };
}

/**
 * fetchSessionDrivers — roster with team colours + acronyms for the dots.
 * @returns {Promise<Array>} raw OpenF1 driver rows (may be empty)
 */
export async function fetchSessionDrivers(sessionKey, { signal } = {}) {
  if (!sessionKey) return [];
  const res = await rawGet(`/drivers?session_key=${sessionKey}`, { signal, ttl: 60_000 });
  return Array.isArray(res.body) ? res.body : [];
}

/** Build the `date>=` filter. NOTE: OpenF1 wants the operator un-encoded next to
 * the field, e.g. `lap_number>=8`; we keep `>=` literal and only encode the
 * value. If a future OpenF1 change rejects this, switch to `%3E=`. */
function sincePath(base, windowSec) {
  const since = new Date(Date.now() - windowSec * 1000).toISOString();
  return `${base}&date>=${encodeURIComponent(since)}`;
}

/**
 * fetchCarPositions — latest [x,y] per driver over the last `windowSec` seconds.
 * @returns {Promise<Map<number,{x:number,y:number,date:string}>>}
 */
export async function fetchCarPositions(sessionKey, { signal, windowSec = 4 } = {}) {
  if (!sessionKey) return new Map();
  const res = await rawGet(sincePath(`/location?session_key=${sessionKey}`, windowSec), { signal });
  return latestPerDriver(Array.isArray(res.body) ? res.body : []);
}

/**
 * fetchOutlineTrace — a chunk of ONE driver's positions, to trace the live
 * track outline in the same telemetry frame as the dots. A ~3-minute window
 * comfortably covers a full lap on every circuit.
 * @returns {Promise<Array<[number,number]>>}
 */
export async function fetchOutlineTrace(sessionKey, driverNumber, { signal, windowSec = 200 } = {}) {
  if (!sessionKey || driverNumber == null) return [];
  const res = await rawGet(
    sincePath(`/location?session_key=${sessionKey}&driver_number=${driverNumber}`, windowSec),
    { signal }
  );
  const rows = Array.isArray(res.body) ? res.body : [];
  return rows
    .filter((r) => Number.isFinite(r.x) && Number.isFinite(r.y) && !(r.x === 0 && r.y === 0))
    .map((r) => [r.x, r.y]);
}

/** '#' + OpenF1 team_colour (bare hex), falling back to a neutral grey. */
export function driverColour(driver) {
  const hex = driver?.team_colour;
  return hex ? `#${hex}` : '#9ca3af';
}
